import { OperationResult } from '@tb/auth';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import 'rxjs/Rx';
import { Injectable } from '@angular/core';
import { ServiceUtilsService } from './service-utils.service';
import { throwError } from 'rxjs';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UrlMaker } from 'src/app/commons/url-maker';
import { ExportDataBodyContent, ImportDataBodyContent } from 'src/app/database/helpers/database-helpers';
import { map, catchError } from 'rxjs/operators';

@Injectable()
export class ImportExportService {
    //gwamUrl: string;
    storeApiUrl: string;

    // --------------------------------------------------------------------------------
    constructor(private http: HttpClient, private serviceUtilsService: ServiceUtilsService) {
        this.http = http;
        this.storeApiUrl = environment.settings.storeApiUrl;
    }

    // import default/sample data
    // --------------------------------------------------------------------------------
    importData(subscriptionKey: string, body: ImportDataBodyContent): Observable<OperationResult> {
        const bodyString = JSON.stringify(body);
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('database')
            .appendSegment('import')
            .appendSegment(subscriptionKey)
            .toString();

        return this.http
            .post<OperationResult>(url, bodyString, { headers: headers })
            .pipe(catchError((error: any) => throwError(error || 'server error (importData)')));
    }

    // get list of configurations for default/sample data (INTL configuration files are added in any case)
    // --------------------------------------------------------------------------------
    getConfigurations(
        subscriptionKey: string,
        configType: string,
        isoCountry: string
    ): Observable<Array<{ isoCountry: string; configurations: Array<string> }>> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('database')
            .appendSegment('configurations')
            .appendSegment(subscriptionKey)
            .appendSegment(configType)
            .appendSegment(isoCountry)
            .toString();

        return this.http
            .get<Array<{ isoCountry: string; configurations: Array<string> }>>(url, { headers: headers })
            .pipe(catchError((error: any) => throwError(error || 'server error (getConfigurations)')));
    }

    // --------------------------------------------------------------------------------
    getListAppsMods(subscriptionKey: string): Observable<OperationResult> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('database')
            .appendSegment('export')
            .appendSegment('getlistappsmods')
            .appendSegment(subscriptionKey)
            // .appendSegment('2')
            .toString();

        return this.http
            .get<OperationResult>(url, { headers: headers })
            .pipe(catchError((error: any) => throwError(error || 'server error (getListAppsMods)')));
    }

    // --------------------------------------------------------------------------------
    getFilesListAppsMods(subscriptionKey: string, configType: string, iso: string, configuration: string): Observable<OperationResult> {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const url = new UrlMaker()
          .appendBaseUrl(this.storeApiUrl)
          .appendSegment('database')
          .appendSegment('import')
          .appendSegment('getfileslistforappsmods')
          .appendSegment(subscriptionKey)
          .appendSegment(configType)
          .appendSegment(iso)
          .appendSegment(configuration)
          .toString();

      return this.http
          .get<OperationResult>(url, { headers: headers })
          .pipe(catchError((error: any) => throwError(error || 'server error (getFilesListAppsMods)')));
  }

    // upload zip file in redis
    // --------------------------------------------------------------------------------
    uploadFiles(subscriptionKey: string, formData: FormData): Observable<OperationResult> {
        const url = new UrlMaker()
            .appendBaseUrl(environment.settings.gwamAppApiUrl)
            .appendSegment('database')
            .appendSegment('upload')
            .appendSegment(subscriptionKey)
            .toString();

        return this.http.post<OperationResult>(url, formData).pipe(
            catchError((error: HttpErrorResponse) => {
                let errorDetail = error.message;

                if (error.status === 401) {
                    return throwError('Unauthorized: ' + errorDetail);
                }
                if (error.status === 403) {
                    return throwError('Forbidden resource: ' + errorDetail);
                }

                return throwError(errorDetail || 'server error (uploadFiles)');
            })
        );
    }

    // export data
    // --------------------------------------------------------------------------------
    exportData(subscriptionKey: string, body: ExportDataBodyContent): Observable<OperationResult> {
        const bodyString = JSON.stringify(body);
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        const url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('database')
            .appendSegment('export')
            .appendSegment(subscriptionKey)
            .toString();

        return this.http
            .post<OperationResult>(url, bodyString, { headers: headers })
            .pipe(catchError((error: any) => throwError(error || 'server error (exportData)')));
    }

    // returns the list of keys about exported zip files in redis
    // --------------------------------------------------------------------------------
    getDownloadList(accountName: string, subscriptionKey: string): Observable<OperationResult> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

        const url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('database')
            .appendSegment('export')
            .appendSegment('list')
            .appendSegment(accountName)
            .appendSegment(subscriptionKey)
            .toString();

        return this.http
            .get<OperationResult>(url, { headers: headers })
            .pipe(catchError((error: any) => throwError(error || 'server error (getExportList)')));
    }

    // // check if exists download file in redis
    // // --------------------------------------------------------------------------------
    // existsDownload(accountName: string, subscriptionKey: string, fileKey: string): Observable<OperationResult> {
    //     const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    //     const url = new UrlMaker()
    //     .appendBaseUrl(this.storeApiUrl)
    //     .appendSegment('database')
    //     .appendSegment('download')
    //     .appendSegment('exists')
    //     .appendSegment(accountName)
    //     .appendSegment(subscriptionKey)
    //     .appendSegment(fileKey)
    //     .toString();

    //     return this.http
    //         .get<OperationResult>(url, { headers: headers })
    //         .pipe(catchError((error: any) => throwError(error || 'server error (existsDownload)')));
    // }

    // --------------------------------------------------------------------------------
    download(file: string, subscriptionKey: string) {
        const url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('database')
            .appendSegment('download')
            .appendSegment(subscriptionKey)
            .appendSegment(file)
            .toString();

        return this.http
            .get(url, { responseType: 'arraybuffer' })
            .pipe(catchError((error: any) => throwError(error || 'server error (download)')));
    }

    // // delete download from redis
    // // --------------------------------------------------------------------------------
    // deleteDownload(fileKey: string): Observable<OperationResult> {
    //     const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    //     let url = new UrlMaker()
    //         .appendBaseUrl(this.storeApiUrl)
    //         .appendSegment('delete')
    //         .appendSegment('webdav')
    //         .appendSegment(fileKey)
    //         .toString();

    //     return this.http
    //         .post<OperationResult>(url, { headers: headers })
    //         .pipe(catchError((error: any) => throwError(error || 'server error (deleteDownload)')));
    // }

    // --------------------------------------------------------------------------------
    getGrafanaLogsToPromise(subscriptionKey: string, accountName: string): Promise<OperationResult> {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        let url = new UrlMaker()
            .appendBaseUrl(this.storeApiUrl)
            .appendSegment('grafanalogs')
            .appendSegment(subscriptionKey)
            .appendSegment(accountName)
            .toString();

        return this.http.get<OperationResult>(url, { headers: headers }).toPromise();
    }
}
